const MAX_BYTES = 150 * 1024;

function dataUrlByteSize(dataUrl) {
  const base64 = dataUrl.split(",")[1] || "";
  return Math.ceil((base64.length * 3) / 4);
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = src;
  });
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

function drawToDataUrl(img, width, height, quality) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", quality);
}

/**
 * Compress an image file to a JPEG data URL stored at max 150KB.
 */
export async function compressImageFile(file, maxBytes = MAX_BYTES) {
  if (!file?.type?.startsWith("image/")) {
    throw new Error("Please select a valid image file (JPG, PNG, WebP).");
  }

  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImage(dataUrl);

  let width = img.width;
  let height = img.height;
  const maxDim = 900;
  if (width > maxDim || height > maxDim) {
    const scale = maxDim / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  let quality = 0.85;
  let result = drawToDataUrl(img, width, height, quality);

  while (dataUrlByteSize(result) > maxBytes && quality > 0.35) {
    quality -= 0.08;
    result = drawToDataUrl(img, width, height, quality);
  }

  while (dataUrlByteSize(result) > maxBytes && width > 160) {
    width = Math.round(width * 0.85);
    height = Math.round(height * 0.85);
    result = drawToDataUrl(img, width, height, quality);
  }

  if (dataUrlByteSize(result) > maxBytes) {
    throw new Error("Could not compress image below 150KB. Try a smaller photo.");
  }

  return result;
}

export function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

export function dataUrlToBlob(dataUrl) {
  const [header, base64] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

export { MAX_BYTES, dataUrlByteSize };
